import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { STATUS, PRIORITY, colorFor, initialsOf, slaPctOf } from "@/lib/mock";
import TicketDrawer from "@/components/TicketDrawer";

function slaColor(pct) {
  if (pct >= 80) return "#EF4444";
  if (pct >= 60) return "#F59E0B";
  return "#10B981";
}

export default function Queue({ tickets = [], categories = [], onChanged, requesterOnly = false }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(null);

  const filtered = useMemo(() => {
    return tickets.filter((t) => {
      if (status !== "all" && t.status !== status) return false;
      if (priority !== "all" && t.priority !== priority) return false;
      if (cat !== "all" && String(t.category?.id) !== String(cat)) return false;
      if (q) {
        const needle = q.toLowerCase();
        const hay = `${t.ticket_no} ${t.title} ${t.requester?.name || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [tickets, q, status, priority, cat]);

  return (
    <div className="glass rounded-2xl p-4 sm:p-5" data-testid="queue">
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="queue-search"
            placeholder="Search by ID, title, requester…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 rounded-xl bg-white/70 dark:bg-white/5 border-white/70 dark:border-white/10"
          />
        </div>
        <div className="grid grid-cols-3 md:flex gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="queue-filter-status" className="w-full md:w-[150px] rounded-xl bg-white/70 dark:bg-white/5">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.values(STATUS).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger data-testid="queue-filter-priority" className="w-full md:w-[150px] rounded-xl bg-white/70 dark:bg-white/5">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {Object.values(PRIORITY).map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger data-testid="queue-filter-category" className="w-full md:w-[170px] rounded-xl bg-white/70 dark:bg-white/5">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-x-auto -mx-2">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-zinc-200/60 dark:border-white/5 hover:bg-transparent">
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Ticket</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Title</TableHead>
              {!requesterOnly && <TableHead className="text-mono text-[10px] uppercase tracking-widest">Requester</TableHead>}
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Category</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Priority</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Assignee</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest text-right">SLA</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((t, i) => {
              const st = STATUS[t.status] || STATUS.open;
              const pr = PRIORITY[t.priority] || PRIORITY.medium;
              const catName = t.category?.name || "General";
              const catColor = colorFor(t.category?.id || catName);
              const sla = t.sla_pct ?? slaPctOf(t);
              const tech = t.assignee;
              return (
                <motion.tr
                  key={t.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i, 12) * 0.02 }}
                  onClick={() => setOpen(t)}
                  className="cursor-pointer border-b border-zinc-200/60 dark:border-white/5 hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
                  data-testid={`queue-row-${t.ticket_no || t.id}`}
                >
                  <TableCell className="text-mono text-xs whitespace-nowrap">{t.ticket_no}</TableCell>
                  <TableCell className="max-w-[380px]">
                    <div className="font-medium truncate">{t.title}</div>
                  </TableCell>
                  {!requesterOnly && (
                    <TableCell>
                      <div className="text-sm">{t.requester?.name || "—"}</div>
                      <div className="text-[11px] text-muted-foreground">{t.requester?.department || ""}</div>
                    </TableCell>
                  )}
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: catColor }} />
                      {catName}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full"
                          style={{ background: `${pr.color}18`, color: pr.color }}>
                      {pr.label}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className="h-2 w-2 rounded-full" style={{ background: st.color }} />
                      {st.label}
                    </span>
                  </TableCell>
                  <TableCell>
                    {tech ? (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-[10px] font-bold" style={{ background: colorFor(tech.id), color: "white" }}>
                            {initialsOf(tech.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs truncate">{tech.name}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-black/5 dark:bg-white/10 overflow-hidden">
                        <div
                          className="h-full"
                          style={{
                            width: `${Math.min(100, sla)}%`,
                            background: slaColor(sla),
                          }}
                        />
                      </div>
                      <span className="text-mono text-[11px] w-9 text-right">{sla}%</span>
                    </div>
                  </TableCell>
                </motion.tr>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">
                  No tickets match your filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
        <div>Showing <b className="text-foreground">{filtered.length}</b> of {tickets.length} tickets</div>
        <div className="text-mono uppercase tracking-widest">the queue</div>
      </div>

      <TicketDrawer ticketId={open?.id} onClose={() => setOpen(null)} onChanged={() => onChanged?.()} />
    </div>
  );
}
